import type {
  HealthResponse,
  PortDaysResponse,
  PortsResponse,
  ShipDetailResponse,
  ShipNow,
  StatsResponse,
  TracksResponse,
  TrailPoint,
} from "@cruise/core";
import { useEffect, useMemo, useState } from "react";
import { getJson, joinTrail, LIVE_TRAIL_MS, toTrailPoint, useLiveShips, usePolling } from "./api";
import { CanvasMap, type MapMode } from "./map/CanvasMap";
import { CallsStrip, NearbyShipsCard, PortDayCard, PortListCard, ShipCard } from "./panels/CruisePanels";
import { DataHealthCard, FleetTable, OpsStrip, PortWeekCard, TrackingCard } from "./panels/OpsPanels";
import { TopBar } from "./TopBar";

const FOLLOWED_KEY = "harborclock.followedImo";
const ALL_TRAILS_KEY = "harborclock.allTrails";
const WEEK_REFRESH_MS = 5 * 60_000;
/** Hours of trail per moving ship when all trails are on. */
const TRAIL_HOURS = LIVE_TRAIL_MS / 3600_000;
/** Live positions extend trails between reloads. */
const TRACKS_REFRESH_MS = 5 * 60_000;

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // private window or blocked storage: the choice still applies for this visit
  }
}

/** A ship worth showing first: in port now, else moving, else anything recently heard. */
function defaultShip(ships: ShipNow[]): ShipNow | undefined {
  return (
    ships.find((s) => s.state === "stopped" && s.nearPort) ??
    ships.find((s) => s.state === "under-way" && s.nextCall) ??
    ships.find((s) => s.state === "under-way") ??
    ships.find((s) => s.position)
  );
}

/** GET /v1/ports/:locode/days for the next 7 days, for every port with a readable schedule. */
function usePortWeek(ports: PortsResponse | null, enabled: boolean): PortDaysResponse[] {
  const [weeks, setWeeks] = useState<PortDaysResponse[]>([]);
  const locodes = useMemo(
    () => (ports?.ports ?? []).filter((p) => p.hasSchedule).map((p) => p.locode).join(","),
    [ports],
  );
  useEffect(() => {
    if (!enabled || !locodes) return;
    const controller = new AbortController();
    const load = () => {
      const from = new Date().toISOString().slice(0, 10);
      const to = new Date(Date.now() + 6 * 86_400_000).toISOString().slice(0, 10);
      Promise.all(
        locodes.split(",").map((l) => getJson<PortDaysResponse>(`/v1/ports/${l}/days?from=${from}&to=${to}`, controller.signal)),
      ).then(setWeeks, () => {
        // keep the previous week on a failed refresh
      });
    };
    load();
    const timer = setInterval(load, WEEK_REFRESH_MS);
    return () => {
      controller.abort();
      clearInterval(timer);
    };
  }, [locodes, enabled]);
  return weeks;
}

export function App() {
  const [mode, setMode] = useState<MapMode>(() =>
    new URLSearchParams(window.location.search).get("mode") === "ops" ? "ops" : "cruise",
  );
  const [now, setNow] = useState(() => new Date());
  const { ships, error: shipsError, streamOpen, live } = useLiveShips();
  const ports = usePolling<PortsResponse>("/v1/ports", 60_000);
  const health = usePolling<HealthResponse>("/v1/health", 60_000);
  const [selectedImo, setSelectedImo] = useState<string | null>(() => readStored(FOLLOWED_KEY));
  const [selectedPort, setSelectedPort] = useState<string | null>(null);
  const [allTrails, setAllTrails] = useState(() => readStored(ALL_TRAILS_KEY) === "on");
  const detail = usePolling<ShipDetailResponse>(selectedImo ? `/v1/ships/${selectedImo}` : null, 60_000);
  const tracks = usePolling<TracksResponse>(allTrails ? `/v1/tracks?hours=${TRAIL_HOURS}` : null, TRACKS_REFRESH_MS);
  const weeks = usePortWeek(ports.data, mode === "ops");
  const stats = usePolling<StatsResponse>(mode === "ops" ? "/v1/stats?hours=48&days=14" : null, 60_000);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("ops", mode === "ops");
    const url = new URL(window.location.href);
    if (mode === "ops") url.searchParams.set("mode", "ops");
    else url.searchParams.delete("mode");
    window.history.replaceState(null, "", url);
  }, [mode]);

  // Pick a ship to follow once ships load, if none is stored or the stored one left the registry.
  useEffect(() => {
    if (ships.length === 0) return;
    if (selectedImo && ships.some((s) => s.imo === selectedImo)) return;
    const ship = defaultShip(ships);
    if (ship) setSelectedImo(ship.imo);
  }, [ships, selectedImo]);

  // Default port: the one with the most ships due today.
  useEffect(() => {
    const list = ports.data?.ports;
    if (selectedPort || !list?.length) return;
    const busiest = [...list].sort((a, b) => b.today.shipsScheduled - a.today.shipsScheduled)[0];
    if (busiest) setSelectedPort(busiest.locode);
  }, [ports.data, selectedPort]);

  const selectShip = (imo: string) => {
    setSelectedImo(imo);
    writeStored(FOLLOWED_KEY, imo);
  };

  const toggleAllTrails = (on: boolean) => {
    setAllTrails(on);
    writeStored(ALL_TRAILS_KEY, on ? "on" : "off");
  };

  const ship = ships.find((s) => s.imo === selectedImo) ?? null;
  const portList = ports.data?.ports ?? [];
  const port = portList.find((p) => p.locode === selectedPort) ?? null;
  const timezones = useMemo(() => new Map(portList.map((p) => [p.locode, p.timezone])), [portList]);
  const shipDetail = detail.data?.ship.imo === selectedImo ? detail.data : null;
  const liveShips = ships.filter((s) => s.state === "under-way" || s.state === "stopped").length;
  const error = shipsError ?? ports.error ?? health.error;

  // Followed ship: its 24-hour track, extended by each live position.
  const followedTrack = useMemo(
    () => (selectedImo ? joinTrail((shipDetail?.track ?? []).map(toTrailPoint), live.get(selectedImo)) : []),
    [selectedImo, shipDetail, live],
  );

  // All trails on: the last 2 hours for every ship, extended by live positions.
  const trails = useMemo(() => {
    if (!allTrails) return null;
    const since = Date.now() - LIVE_TRAIL_MS;
    const loaded = new Map((tracks.data?.tracks ?? []).map((t) => [t.imo, t.points]));
    const out = new Map<string, TrailPoint[]>();
    for (const imo of new Set([...loaded.keys(), ...live.keys()])) {
      const points = joinTrail(loaded.get(imo) ?? [], live.get(imo), since);
      // One point is enough: the map draws the trail on to the ship's dot.
      if (points.length > 0) out.set(imo, points);
    }
    return out;
  }, [allTrails, tracks.data, live]);

  return (
    <>
      <CanvasMap
        mode={mode}
        ships={ships}
        ports={portList}
        selectedImo={selectedImo}
        selectedPort={selectedPort}
        track={followedTrack}
        trails={trails}
        onSelectShip={selectShip}
        onSelectPort={setSelectedPort}
      />
      <TopBar
        mode={mode}
        onMode={setMode}
        now={now}
        streamOpen={streamOpen}
        shipsLive={liveShips}
        allTrails={allTrails}
        onAllTrails={toggleAllTrails}
      />
      {error && <div className="error-banner glass">API unreachable: {error}</div>}

      {mode === "cruise" ? (
        <>
          <div id="leftcol" className="side">
            <ShipCard ship={ship} detail={shipDetail} timezones={timezones} now={now.getTime()} />
          </div>
          <div id="rightcol" className="side">
            <PortDayCard port={port} onSelectShip={selectShip} />
            <NearbyShipsCard port={port} onSelectShip={selectShip} />
            <PortListCard ports={portList} selected={selectedPort} onSelect={setSelectedPort} />
          </div>
          <CallsStrip ship={ship} detail={shipDetail} timezones={timezones} now={now.getTime()} />
        </>
      ) : (
        <>
          <div id="leftcol" className="side">
            <TrackingCard stats={stats.data} now={now.getTime()} />
            <FleetTable ships={ships} health={health.data} selectedImo={selectedImo} onSelectShip={selectShip} />
          </div>
          <div id="rightcol" className="side">
            <PortWeekCard weeks={weeks} />
            <DataHealthCard health={health.data} now={now.getTime()} />
          </div>
          <OpsStrip health={health.data} ports={portList} now={now.getTime()} />
        </>
      )}
    </>
  );
}
